// Transformation-only: HEIC→JPEG + EXIF strip + PDF/PNG passthrough.
// Size/mime валидация — отдельно через ClientFileSchema ПОСЛЕ prepareForUpload.
// Source of truth: docs/specs/sprint-4/stage-4-frontend.md §3.2 + stage-1-contracts.md §6.3.

import heic2any from 'heic2any';
// @ts-expect-error — piexifjs не имеет официальных типов
import piexif from 'piexifjs';

export type ImagePrepError = { kind: 'heic_conversion_failed'; cause: unknown };
export type ImagePrepResult = { blob: Blob; name: string; mimeType: string };

export async function prepareForUpload(
  picked: { blob: Blob; name: string; mimeType: string },
): Promise<ImagePrepResult | ImagePrepError> {
  let blob = picked.blob;
  let mimeType = picked.mimeType.toLowerCase();
  let name = picked.name;

  if (mimeType === 'image/heic' || mimeType === 'image/heif') {
    try {
      const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.85 });
      blob = Array.isArray(converted) ? converted[0] : converted;
      mimeType = 'image/jpeg';
      name = name.replace(/\.(heic|heif)$/i, '.jpg');
    } catch (cause) {
      return { kind: 'heic_conversion_failed', cause };
    }
  }

  if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
    blob = await stripExifFromJpeg(blob);
    mimeType = 'image/jpeg';
  }

  return { blob, name, mimeType };
}

async function stripExifFromJpeg(blob: Blob): Promise<Blob> {
  const dataUrl = await blobToDataUrl(blob);
  const stripped = piexif.remove(dataUrl);
  return dataUrlToBlob(stripped);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:([^;]+)/)?.[1] ?? 'image/jpeg';
  const binary = atob(b64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([buffer], { type: mime });
}
